import { Field, FieldProps, Formik } from 'formik';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { stylesheet, classes } from 'typestyle';

import { Input, Select, Switch } from '@src/controls';
import { withFormItem } from '@src/decorators';
import { AutoSaveForm, inputStyle, MakeAutoSaveFormikEnhancer, widerLayout } from '@src/forms';

import { CategoryLabelModel } from '@src/models';
import { configActions } from '@src/modules/config';
import { dispatchOnDraftSiteId } from '@src/utils/conditional-dispatchers';
import { getFormikError } from '@src/utils/forms';
import { object, string } from 'yup';
import { matchHexColor } from '@src/utils';
import { labelIconsOptions } from './label-icon';
import { useFormatMessage } from '@src/i18n';
import { LabelPreview } from './label-preview';
import { labelTypesOptions } from './label-options';

const InputField = withFormItem(Input, widerLayout);
const SelectField = withFormItem(Select, widerLayout);
const SwitchField = withFormItem(Switch, widerLayout);

const TypedAutoSaveEnhancer = MakeAutoSaveFormikEnhancer<LabelDetailsFormikValues>();

type LabelDetailsFormikValues = Pick<CategoryLabelModel, 'name' | 'type' | 'color' | 'icon'> & {
  active: boolean;
};

interface Props {
  categoryId: string;
  label: CategoryLabelModel;
}

const editLabelSchema = object({
  name: string().required(),
  type: string().required(),
  color: string().test('is-hex-color', 'HEX_VALUE_ERROR', value => matchHexColor(value ?? '')),
});
export const LabelDetailsEdit: React.FC<Props> = ({ label, categoryId }) => {
  const formatMessage = useFormatMessage();
  const handleUpdate = (values: Partial<LabelDetailsFormikValues>) => {
    dispatchOnDraftSiteId(configActions.updateCategoryLabelDetailsRequest)({
      model: {
        categoryId,
        labelId: label.id,
        name: values.name,
        type: values.type,
        color: values.type === 'LABEL_TYPE_SWAN' ? '' : values.color,
        icon: values.type === 'LABEL_TYPE_SWAN' ? '' : values.icon,
        state: values.active ? 'ACTIVE' : 'INACTIVE',
      },
    });
  };
  return (
    <Formik
      key={label.id}
      initialValues={{
        name: label.name,
        type: label.type,
        active: label.state === 'ACTIVE',
        color: label.color || '',
        icon: label.icon || '',
      }}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSubmit={() => {}}
      validationSchema={editLabelSchema}
    >
      {formikProps => (
        <AutoSaveForm>
          <InputField
            label={<FormattedMessage id="ID" />}
            className={inputStyle}
            value={label.id}
            disabled={true}
          />
          <TypedAutoSaveEnhancer
            onSave={handleUpdate}
            name="name"
            render={({ name, onBlur, onKeyDown }) => (
              <Field name={name}>
                {({ field }: FieldProps<LabelDetailsFormikValues['name']>) => (
                  <InputField
                    label={<FormattedMessage id="NAME" />}
                    className={inputStyle}
                    {...field}
                    error={getFormikError(formikProps.touched.name, formikProps.errors.name)}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                  />
                )}
              </Field>
            )}
          />

          <TypedAutoSaveEnhancer
            name="type"
            onSave={handleUpdate}
            render={({ name, onInstantChange }) => (
              <Field name={name}>
                {({ field }: FieldProps<LabelDetailsFormikValues['type']>) => (
                  <SelectField
                    {...field}
                    options={labelTypesOptions}
                    onChange={(value: string) => onInstantChange(value)}
                    label={<FormattedMessage id="TYPE" />}
                    labelTooltip={<FormattedMessage id="CATEGORY_LABEL_MODAL.TYPE_TOOLTIP" />}
                    className={inputStyle}
                  />
                )}
              </Field>
            )}
          />
          <TypedAutoSaveEnhancer
            onSave={handleUpdate}
            name="color"
            render={({ name, onBlur, onKeyDown }) => (
              <Field name={name}>
                {({
                  field: { value, ...field },
                }: FieldProps<LabelDetailsFormikValues['color']>) => (
                  <InputField
                    {...field}
                    // hide only the value for swan label while keeping it in the form state
                    value={formikProps.values.type === 'LABEL_TYPE_SWAN' ? '' : value}
                    disabled={formikProps.values.type === 'LABEL_TYPE_SWAN'}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    label={<FormattedMessage id="COLOR" />}
                    labelAlign="left"
                    error={formikProps.errors.color ? formatMessage('HEX_VALUE_ERROR') : ''}
                    className={classes(
                      inputStyle,
                      getFormikError(formikProps.touched.color, formikProps.errors.color) &&
                        styles.inputError
                    )}
                  />
                )}
              </Field>
            )}
          />
          <TypedAutoSaveEnhancer
            onSave={handleUpdate}
            name="icon"
            render={({ name, onBlur }) => (
              <Field name={name}>
                {({ field: { value, ...field } }: FieldProps<LabelDetailsFormikValues['icon']>) => (
                  <SelectField
                    allowClear
                    label={<FormattedMessage id="ICON" />}
                    options={labelIconsOptions}
                    labelAlign="left"
                    error={getFormikError(formikProps.touched.icon, formikProps.errors.icon)}
                    {...field}
                    // hide only the value for swan label while keeping it in the form state
                    value={formikProps.values.type === 'LABEL_TYPE_SWAN' ? '' : value}
                    disabled={formikProps.values.type === 'LABEL_TYPE_SWAN'}
                    onBlur={onBlur}
                    onChange={value => {
                      formikProps.setFieldValue('icon', value);
                    }}
                    className={classes(inputStyle, styles.icon)}
                  />
                )}
              </Field>
            )}
          />
          <LabelPreview
            label={formikProps.values}
            columns={[6, 18]}
            isValid={formikProps.isValid}
          />
          <SwitchField
            label={<FormattedMessage id="ACTIVE" />}
            checked={label.state === 'ACTIVE'}
            onChange={value => {
              handleUpdate({ ...formikProps.values, active: value });
            }}
          />
        </AutoSaveForm>
      )}
    </Formik>
  );
};

const styles = stylesheet({
  inputError: {
    border: '1px solid #f5222d',
    $nest: {
      '&:hover': {
        border: '1px solid #F5222D',
      },
    },
  },
  icon: {
    marginLeft: '4px',
  },
});
